/**
 * Floating "From IVR" screen pop on the agent's ringing / active call.
 * The on-prem IVRS posts the caller's context (member id, verification,
 * intent, IVR path) to /integration/v1/call-context just before it
 * transfers the call; GET /agent/integration/call-context matches it to this
 * call by caller number and adds the member summary from the core system.
 * Fail-open: when the core system is unreachable the card says so and the
 * agent verifies the caller the usual way.
 */
import { Fragment, useEffect, useState, type JSX } from 'react'
import { AlertTriangle, BadgeCheck, Building2, ChevronDown, ChevronUp, PhoneForwarded, X } from 'lucide-react'
import { api } from '../../api/client.js'

interface CallContext {
  contextId: string
  ani: string
  memberId: string | null
  verified: boolean
  verificationMethod: string | null
  intent: string | null
  language: string | null
  ivrPath: string[]
  attributes: Record<string, string | number | boolean>
  createdAt: string
}

interface MemberResult { ok: boolean; data: Record<string, unknown> | null; error: string | null; source: string; durationMs: number }

const LANG: Record<string, string> = { fil: 'Filipino', tl: 'Tagalog', en: 'English', ceb: 'Cebuano', ilo: 'Ilocano', hi: 'Hindi' }
const pretty = (s: string): string => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/** Known member-summary fields first, then anything else the adapter returned (scalars only). */
function memberRows(d: Record<string, unknown>): [string, string][] {
  const order = ['memberId', 'displayName', 'memberType', 'status', 'preferredLanguage', 'employer', 'branch']
  const keys = [...order.filter((k) => k in d), ...Object.keys(d).filter((k) => !order.includes(k) && !['flags', 'source', 'retrievedAt'].includes(k))]
  return keys.filter((k) => ['string', 'number', 'boolean'].includes(typeof d[k])).slice(0, 8).map((k) => [pretty(k.replace(/([a-z])([A-Z])/g, '$1 $2')), String(d[k])])
}

export default function IvrContextCard({ callId, ani }: { callId: string | null; ani: string | null }): JSX.Element | null {
  const [context, setContext] = useState<CallContext | null>(null)
  const [member, setMember] = useState<MemberResult | null>(null)
  const [hidden, setHidden] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    setContext(null)
    setMember(null)
    setHidden(false)
    if (!callId && !ani) return
    let cancelled = false
    let tries = 0
    // The IVRS posts the context a moment before the transfer rings; retry briefly.
    const load = (): void => {
      const q = new URLSearchParams({ ...(callId ? { callId } : {}), ...(ani ? { ani } : {}) })
      void api.get<{ context: CallContext | null; member: MemberResult | null }>(`/agent/integration/call-context?${q.toString()}`)
        .then((r) => {
          if (cancelled) return
          if (r.context) { setContext(r.context); setMember(r.member) }
          else if (tries++ < 3) setTimeout(load, 1500)
        })
        .catch(() => { /* integration not configured */ })
    }
    load()
    return () => { cancelled = true }
  }, [callId, ani])

  if (!context || hidden) return null
  const m = member?.ok && member.data && typeof member.data === 'object' ? member.data : null
  const flags = Array.isArray(m?.['flags']) ? (m!['flags'] as unknown[]).map(String) : []
  const attrs = Object.entries(context.attributes ?? {})

  return (
    <div className="fixed right-6 top-20 z-[900] w-[360px] overflow-hidden rounded-[3px] bg-white shadow-[0_16px_48px_-12px_rgba(15,23,42,0.35)] ring-1 ring-teal-200">
      <header className="flex items-center gap-2 bg-teal-700 px-3 py-2 text-white">
        <PhoneForwarded className="h-4 w-4" />
        <p className="flex-1 text-[12.5px] font-semibold">Transferred from IVR</p>
        {context.verified && <span className="inline-flex items-center gap-1 rounded-[3px] bg-white/15 px-1.5 py-0.5 text-[11px] font-semibold"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
        <button type="button" aria-label={collapsed ? 'Expand' : 'Collapse'} className="rounded-[3px] p-1 hover:bg-white/10" onClick={() => setCollapsed((v) => !v)}>
          {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <button type="button" aria-label="Dismiss" className="rounded-[3px] p-1 hover:bg-white/10" onClick={() => setHidden(true)}><X className="h-4 w-4" /></button>
      </header>
      {!collapsed && (
        <div className="grid gap-3 px-3 py-3 text-[12.5px] text-slate-700">
          <div className="flex flex-wrap gap-1.5">
            {context.intent && <span className="rounded-[3px] bg-blue-50 px-2 py-0.5 text-[11.5px] font-semibold text-blue-700 ring-1 ring-blue-200">{pretty(context.intent)}</span>}
            {context.language && <span className="rounded-[3px] bg-slate-100 px-2 py-0.5 text-[11.5px] font-semibold text-slate-700">{LANG[context.language] ?? context.language}</span>}
            <span className={`rounded-[3px] px-2 py-0.5 text-[11.5px] font-semibold ${context.verified ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200'}`}>
              {context.verified ? `Identity verified${context.verificationMethod ? ` · ${pretty(context.verificationMethod.toLowerCase())}` : ''}` : 'Not verified in IVR'}
            </span>
          </div>

          {m ? (
            <div className="rounded-[3px] bg-slate-50 p-2.5 ring-1 ring-slate-200">
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Building2 className="h-3.5 w-3.5" /> Member · {member!.source}</p>
              <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-0.5">
                {memberRows(m).map(([k, v]) => <Fragment key={k}><dt className="text-slate-400">{k}</dt><dd className="truncate font-medium text-slate-900">{v}</dd></Fragment>)}
              </dl>
              {flags.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{flags.map((f) => <span key={f} className="rounded-[3px] bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-900">{pretty(f.toLowerCase())}</span>)}</div>}
            </div>
          ) : member && !member.ok ? (
            <p className="flex items-start gap-1.5 rounded-[3px] bg-amber-50 px-2.5 py-2 text-[12px] text-amber-900 ring-1 ring-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Member details unavailable from {member.source} right now. Member id <span className="font-mono">{context.memberId}</span> — verify the caller with the standard script.</span>
            </p>
          ) : context.memberId ? (
            <p className="text-[12px]">Member id <span className="font-mono font-semibold">{context.memberId}</span></p>
          ) : null}

          {context.ivrPath.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">IVR path</p>
              <p className="text-[12px] leading-relaxed">{context.ivrPath.map(pretty).join(' › ')}</p>
            </div>
          )}
          {attrs.length > 0 && (
            <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-0.5 text-[12px]">
              {attrs.map(([k, v]) => <Fragment key={k}><dt className="text-slate-400">{pretty(k)}</dt><dd className="truncate text-slate-800">{String(v)}</dd></Fragment>)}
            </dl>
          )}
          <p className="text-[11px] text-slate-400">Caller {context.ani} · context {context.contextId.slice(0, 8)}</p>
        </div>
      )}
    </div>
  )
}
